import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import i18n, {
  SUPPORTED_LANGUAGES,
  LANGUAGE_CODES,
  ENABLED_UI_LANGUAGES,
  ENABLED_UI_LANGUAGE_CODES,
  resolveEnabledLocale,
  changeLanguage as i18nChangeLanguage,
} from '../i18n/i18n';

const LanguageContext = createContext();

/**
 * Field policy types — must match backend field_policies.py
 */
export const FIELD_POLICY = {
  TRANSLATABLE_REQUIRED: 'translatable_required',
  TRANSLATABLE_OPTIONAL: 'translatable_optional',
  FALLBACK_TO_EN: 'fallback_to_en',
  NON_TRANSLATABLE: 'non_translatable',
  DISPLAY_FALLBACK_ONLY: 'display_fallback_only',
};

/**
 * Extract a localized string from a multilingual API field.
 * Handles both multilingual objects and legacy strings.
 * Fallback chain: requested lang → en → first non-empty → ""
 *
 * This is the RENDER-TIME fallback. It does NOT modify stored data.
 */
export const getLocalizedField = (field, lang = 'en') => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object') {
    // Try requested language
    const val = field[lang];
    if (val && typeof val === 'string' && val.trim()) return val;
    // Fallback to English
    const enVal = field['en'];
    if (enVal && typeof enVal === 'string' && enVal.trim()) return enVal;
    // Fallback to any non-empty
    for (const v of Object.values(field)) {
      if (v && typeof v === 'string' && v.trim()) return v;
    }
  }
  return '';
};

/**
 * Compute translation completeness for a single multilingual field (frontend).
 */
export const computeFieldCompleteness = (field) => {
  if (!field || typeof field !== 'object') {
    const hasValue = typeof field === 'string' && field.trim();
    return { filled: hasValue ? 1 : 0, total: LANGUAGE_CODES.length, missing: hasValue ? LANGUAGE_CODES.slice(1) : LANGUAGE_CODES };
  }
  const filled = [];
  const missing = [];
  for (const code of LANGUAGE_CODES) {
    const v = field[code];
    if (v && typeof v === 'string' && v.trim()) {
      filled.push(code);
    } else {
      missing.push(code);
    }
  }
  return { filled: filled.length, total: LANGUAGE_CODES.length, filledLangs: filled, missing };
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // TREATX-LOCALE-ENABLEMENT-01 — clamp initial resolution to the
    // UI-enabled set so a stale disabled-locale preference (e.g. an
    // old `de` marker in localStorage) can never render a half-
    // translated app.
    const i18nLang = i18n.language;
    if (i18nLang && LANGUAGE_CODES.includes(i18nLang)) return resolveEnabledLocale(i18nLang);
    const stored = localStorage.getItem('i18nextLng');
    if (stored && LANGUAGE_CODES.includes(stored)) return resolveEnabledLocale(stored);
    return resolveEnabledLocale('en');
  });

  // TREATX-PRODUCT-LANGUAGES-ADMIN-01 — the frontend `ENABLED_UI_LANGUAGES`
  // constant is now only a bootstrap/emergency fallback. The runtime
  // authority is the backend `/api/config/enabled-locales` endpoint,
  // whose value can be flipped by TreatX Admin without redeploying
  // the frontend. We hydrate this at mount and re-check whenever the
  // page regains focus (so admin changes propagate on next tab focus
  // without requiring a hard reload).
  const [enabledLocaleCodes, setEnabledLocaleCodes] = useState(ENABLED_UI_LANGUAGE_CODES);

  const _hydrateEnabledLocales = useCallback(async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      if (!base) return;
      const res = await fetch(`${base}/api/config/enabled-locales`, {
        cache: 'no-store',
      });
      if (!res.ok) return;
      const data = await res.json();
      const codes = Array.isArray(data?.enabled_locales)
        ? data.enabled_locales.filter((c) => LANGUAGE_CODES.includes(c))
        : null;
      if (codes && codes.length > 0) {
        setEnabledLocaleCodes(codes);
        // Clamp the current language against the freshly-hydrated
        // enabled set. If admin has disabled the locale the user
        // is currently on, silently fall back to the default.
        if (!codes.includes(language)) {
          const fallback = codes.includes('en') ? 'en' : codes[0];
          setLanguageState(fallback);
          i18nChangeLanguage(fallback);
        }
      }
    } catch (e) {
      // Silent fail — bootstrap/emergency default remains authoritative.
    }
  }, [language]);

  useEffect(() => {
    _hydrateEnabledLocales();
    const onFocus = () => { _hydrateEnabledLocales(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [_hydrateEnabledLocales]);

  useEffect(() => {
    const currentI18nLang = i18n.language;
    if (currentI18nLang && currentI18nLang !== language && LANGUAGE_CODES.includes(currentI18nLang)) {
      setLanguageState(currentI18nLang);
    }
  }, []);

  // TREATX-FIPO-F8_2_C (D-B11 · D-B22) — persist the authenticated
  // user's language selection to the backend SSOT so server-initiated
  // communications (emails / notifications / legal / agreements)
  // honour it. Failures are silent — the client-side preference
  // remains authoritative for the session.
  const _persistPreferenceToBackend = async (code) => {
    try {
      const token = null; // FIND-H-08 cookie handles auth
      if (!token) return; // Anonymous — client-side only (D-B22).
      const base = process.env.REACT_APP_BACKEND_URL;
      if (!base) return;
      await fetch(`${base}/api/user/preferred-language`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ preferred_language: code }),
      });
    } catch (e) {
      // fail-open
    }
  };

  const setLanguage = (newLang) => {
    // TREATX-PRODUCT-LANGUAGES-ADMIN-01 — clamp caller-supplied code
    // against the RUNTIME-hydrated enabled set (not the frontend
    // bootstrap constant). This ensures admin-disabled locales are
    // rejected consistently even before the next page reload.
    const target = String(newLang || '').trim();
    const resolved = enabledLocaleCodes.includes(target)
      ? target
      : (enabledLocaleCodes.includes('en') ? 'en' : (enabledLocaleCodes[0] || 'en'));
    setLanguageState(resolved);
    i18nChangeLanguage(resolved);
    // Fire-and-forget backend sync. D-B22 transparent transfer.
    _persistPreferenceToBackend(resolved);
  };

  // TREATX-FIPO-F8_2_C (D-B22) — post-authentication transparent
  // transfer of the client-side language to the backend SSOT. Runs
  // once per authenticated session (guarded by a per-language
  // marker in localStorage so it never spams the endpoint).
  useEffect(() => {
    try {
      const token = null; // FIND-H-08 cookie handles auth
      if (!token) return;
      const stampKey = `i18nSyncedLang:${language}`;
      if (localStorage.getItem(stampKey) === '1') return;
      _persistPreferenceToBackend(language);
      localStorage.setItem(stampKey, '1');
    } catch (e) {
      // ignore
    }
  }, [language]);

  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      if (lng !== language && LANGUAGE_CODES.includes(lng)) setLanguageState(lng);
    };
    i18n.on('languageChanged', handleLanguageChanged);
    return () => i18n.off('languageChanged', handleLanguageChanged);
  }, [language]);

  const localizeField = (field) => getLocalizedField(field, language);

  const currentLangInfo = SUPPORTED_LANGUAGES.find(l => l.code === language) || SUPPORTED_LANGUAGES[0];

  // Derive the enabled languages from the runtime-hydrated codes,
  // preserving the canonical presentation order defined by
  // SUPPORTED_LANGUAGES. This is what all product selectors consume.
  const enabledLanguages = SUPPORTED_LANGUAGES.filter(l => enabledLocaleCodes.includes(l.code));

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage,
      localizeField,
      supportedLanguages: SUPPORTED_LANGUAGES,
      languageCodes: LANGUAGE_CODES,
      // TREATX-PRODUCT-LANGUAGES-ADMIN-01 — canonical enabled-locale
      // authority for product UI selectors. Value is RUNTIME-hydrated
      // from /api/config/enabled-locales; the frontend
      // ENABLED_UI_LANGUAGE_CODES constant is only a bootstrap fallback.
      enabledLanguages,
      enabledLanguageCodes: enabledLocaleCodes,
      currentLangInfo,
      isRTL: currentLangInfo.direction === 'rtl',
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};
